import React, { FC } from 'react';
import { color, conditionColorMap } from 'utils/colors';

import styles from './SpotClusterMarker.module.scss';

export interface SpotClusterMarkerProps {
  worstCondition?: string | null;
  bestCondition?: string | null;
  spotCount: number;
  onClick?: () => void;
}

const gradientOffsetX = 15;
const gradientOffsetY = 27 - Math.sqrt(24 * 24 - gradientOffsetX * gradientOffsetX);

const topGradientStartPoint = `${27 - gradientOffsetX},${gradientOffsetY}`;
const topGradientEndPoint = `${27 + gradientOffsetX},${gradientOffsetY}`;
const bottomGradientStartPoint = `${27 - gradientOffsetX},${54 - gradientOffsetY}`;
const bottomGradientEndPoint = `${27 + gradientOffsetX},${54 - gradientOffsetY}`;

const topGradientPath = `M${topGradientStartPoint} A24,24,0,0,1,${topGradientEndPoint}`;
const bottomGradientPath = `M${bottomGradientStartPoint} A24,24,0,0,0,${bottomGradientEndPoint}`;
const leftArcPath = `M${topGradientStartPoint} A24,24,0,0,0,${bottomGradientStartPoint}`;
const rightArcPath = `M${topGradientEndPoint} A24,24,0,0,1,${bottomGradientEndPoint}`;

const getColorFromColorMap = (
  colorCondition: string | null | undefined,
  colorMap: Map<string, any>,
) =>
  colorCondition && colorMap.has(colorCondition)
    ? colorMap.get(colorCondition).default
    : color('white');

const SpotClusterMarker: FC<SpotClusterMarkerProps> = ({
  spotCount,
  worstCondition = null,
  bestCondition = null,
  onClick,
}) => {
  const worstColor = getColorFromColorMap(worstCondition, conditionColorMap);
  const bestColor = getColorFromColorMap(bestCondition, conditionColorMap);

  // This is necessary to prevent the linearGradient defs from conflicting with each other
  const conditionsGradientId = `ConditionsGradient:${worstCondition}:${bestCondition}`;
  const conditionsGradientStroke = `url(#${conditionsGradientId})`;

  return (
    <svg
      data-testid="spot-cluster-marker"
      className={styles.spotClusterMarker}
      width="54px"
      height="54px"
      onClick={onClick}
    >
      <defs>
        <linearGradient data-testid="spot-cluster-marker-linear-gradient" id={conditionsGradientId}>
          <stop
            data-testid="spot-cluster-marker-linear-gradient-start"
            offset="0%"
            stopColor={worstColor}
          />
          <stop
            data-testid="spot-cluster-marker-linear-gradient-stop"
            offset="100%"
            stopColor={bestColor}
          />
        </linearGradient>
      </defs>
      <g>
        <circle cx="27" cy="27" r="24" />
        <path d={leftArcPath} stroke={worstColor} />
        <path d={topGradientPath} stroke={conditionsGradientStroke} />
        <path d={rightArcPath} stroke={bestColor} />
        <path d={bottomGradientPath} stroke={conditionsGradientStroke} />
      </g>
      <text x="27" y="25">
        {spotCount}
      </text>
    </svg>
  );
};

export default SpotClusterMarker;
